import { HttpErrorResponse } from '@angular/common/http';

export interface ApiErrorResponse {
    title?: string;
    status?: number;
    detail?: string;
    traceId?: string;
    [key: string]: any;
}

export interface ErrorMessageOptions {
    defaultMessage?: string;
    includeStatus?: boolean;
    includeTitle?: boolean;
    statusMessages?: { [statusCode: number]: string };
}

function isApiErrorResponse(body: unknown): body is ApiErrorResponse {
    return body !== null && typeof body === 'object' && ('detail' in body || 'title' in body);
}

function isStringArray(value: unknown): value is string[] {
    return Array.isArray(value) && value.every(item => typeof item === 'string');
}

export const ERROR_SNACKBAR_DURATION = 8000;

function getDefaultStatusMessage(status: number, fallbackMessage: string): string {
    switch (status) {
        case 0:
            return 'Unable to connect to the server. Please check your internet connection and try again.';
        case 400:
            return 'Bad Request: Please check your input and try again.';
        case 401:
            return 'Unauthorized: Please log in and try again.';
        case 403:
            return 'Forbidden: You do not have permission to perform this action.';
        case 404:
            return 'Not Found: The requested resource could not be found.';
        case 409:
            return 'Conflict: The request could not be completed due to a conflict.';
        case 422:
            return 'Validation Error: Please check your input and try again.';
        case 429:
            return 'Too Many Requests: Please wait a moment before trying again.';
        case 500:
            return 'Internal Server Error: Something went wrong on our end.';
        case 502:
            return 'Bad Gateway: Service temporarily unavailable.';
        case 503:
            return 'Service Unavailable: Please try again later.';
        default:
            return fallbackMessage;
    }
}

export function extractErrorMessage(error: HttpErrorResponse | any, options: ErrorMessageOptions = {}): string {
    const {
        defaultMessage = 'An unexpected error occurred. Please try again.',
        includeStatus = false,
        includeTitle = false,
        statusMessages = {}
    } = options;

    if (error instanceof HttpErrorResponse) {
        const errorBody = error.error;

        if (statusMessages[error.status]) {
            return statusMessages[error.status];
        }

        if (Array.isArray(errorBody)) {
            const validMessages = errorBody.filter(msg => typeof msg === 'string' && msg.trim().length > 0);
            return validMessages.length > 0 ? validMessages.join('; ') : defaultMessage;
        }

        if (isApiErrorResponse(errorBody)) {
            const apiError = errorBody;

            if (apiError.detail) {
                let message = apiError.detail;

                if (includeTitle && apiError.title) {
                    message = `${apiError.title}: ${message}`;
                }

                if (includeStatus && error.status) {
                    message = `[${error.status}] ${message}`;
                }

                return message;
            }

            if (apiError.title) {
                return apiError.title;
            }
        }

        return getDefaultStatusMessage(error.status, error.message || defaultMessage);
    }

    if (Array.isArray(error)) {
        const validMessages = error.filter(msg => typeof msg === 'string' && msg.trim().length > 0);
        return validMessages.length > 0 ? validMessages.join('; ') : defaultMessage;
    }

    if (error instanceof Error) {
        return error.message || defaultMessage;
    }

    if (typeof error === 'string') {
        return error;
    }

    if (error && typeof error === 'object') {
        if (error.error && Array.isArray(error.error)) {
            const validMessages = error.error.filter(msg => typeof msg === 'string' && msg.trim().length > 0);
            return validMessages.length > 0 ? validMessages.join('; ') : defaultMessage;
        }

        if (isApiErrorResponse(error.error)) {
            const errorBody = error.error;
            if (errorBody.detail) return errorBody.detail;
            if (errorBody.title) return errorBody.title;
        }

        if (error.detail) return error.detail;
        if (error.message) return error.message;
        if (typeof error.status === 'number') {
            return getDefaultStatusMessage(error.status, error.message || defaultMessage);
        }
    }

    return defaultMessage;
}

export function getUserFacingErrorMessage(
    error: HttpErrorResponse | any,
    defaultMessage: string = 'Something went wrong. Please try again.'
): string {
    return extractErrorMessage(error, {
        defaultMessage,
        includeStatus: false,
        includeTitle: false
    });
}

export function isValidationError(error: HttpErrorResponse | any): boolean {
    if (error instanceof HttpErrorResponse) {
        return error.status === 400 || error.status === 422;
    }
    return false;
}

export function isNetworkError(error: HttpErrorResponse | any): boolean {
    if (error instanceof HttpErrorResponse) {
        return error.status === 0 || error.status >= 500;
    }
    return false;
}

export class ErrorHandler {
    static handleApiError(
        error: HttpErrorResponse | any
    ): {
        message: string;
        isValidationError: boolean;
        isNetworkError: boolean;
        statusCode?: number;
    } {
        return {
            message: extractErrorMessage(error),
            isValidationError: isValidationError(error),
            isNetworkError: isNetworkError(error),
            statusCode: error instanceof HttpErrorResponse ? error.status : undefined
        };
    }

    static formatForLogging(error: any): string {
        if (error instanceof HttpErrorResponse) {
            const errorBody = error.error as ApiErrorResponse;
            return `HTTP ${error.status}: ${error.url} - ${errorBody?.detail ||
                error.message} (TraceId: ${errorBody?.traceId || 'N/A'})`;
        }

        return error?.message || error?.toString() || 'Unknown error';
    }
}
